import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Check, Copy, Pencil, Trash2 } from "lucide-react";
import { createPortal } from "react-dom";
import {
    Accordion,
    Alert,
    Badge,
    Button,
    Form,
    Modal,
    Nav,
    OverlayTrigger,
    Spinner,
    Table,
    Tooltip,
} from "react-bootstrap";
import { AppCheckbox, AppDateField, CheckboxHolder } from "../components/form";
import { authFetch } from "../api/authFetch";
import WorkshopWorkersCrud, { workerRoleLabelUk } from "../components/WorkshopWorkersCrud";
import useAuth from "../hooks/useAuth";
import { useFopLabels } from "../hooks/useFopLabels";

const API = "/api/workshop-payroll";

function normWorkshopLabel(s) {
    return String(s || "")
        .normalize("NFC")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");
}

/** Підпис ролі в зведенні історії виплат (суми згруповані по цеху). */
function historyPayrollRoleGroupLabelUk(role) {
    switch (role) {
        case "seamstress":
            return "Швачки";
        case "cutter":
            return "Закрійниця";
        case "hardware":
            return "Фурнітурниця";
        case "other":
            return "Інше";
        case "_none":
            return "Без працівника";
        default:
            return role ? String(role) : "—";
    }
}

/**
 * Копіювання реквізитів (ПІБ одержувача, ІПН, IBAN).
 * `variant="history"` — компактний рядок у таблиці історії виплат.
 */
function WorkerRequisiteCopyButtons({ worker, variant = "history" }) {
    const rowId = String(worker?._id || "").replace(/\W/g, "") || "row";
    const [lastCopied, setLastCopied] = useState(null);
    const copy = async (text, key) => {
        const t = String(text || "").trim();
        if (!t) return;
        try {
            await navigator.clipboard.writeText(t);
            setLastCopied(key);
            setTimeout(() => setLastCopied((x) => (x === key ? null : x)), 1200);
        } catch {
            /* ignore */
        }
    };
    const pib = String(worker?.recipientName || "").trim();
    const ipn = String(worker?.ipn || "").trim();
    const iban = String(worker?.iban || "").replace(/\s+/g, "").trim();

    const tip = (key, label, disabled) => (
        <Tooltip id={`wp-copy-${rowId}-${key}`} className="wp-req-copy-tip">
            {disabled ? "Немає" : lastCopied === key ? "Скопійовано" : label}
        </Tooltip>
    );

    const btnClass =
        variant === "history"
            ? "py-0 px-1 lh-1 border-0 bg-transparent text-secondary"
            : "py-0 px-1 lh-1";

    const btnStyle =
        variant === "history"
            ? { fontSize: "0.6rem", minHeight: 0, minWidth: 0, textDecoration: "underline dotted" }
            : { fontSize: "0.65rem", minHeight: 0 };

    const mkBtn = (key, label, value, disabled, text = label) => (
        <OverlayTrigger key={key} placement="top" overlay={tip(key, `Копіювати ${label}`, disabled)}>
            <Button
                type="button"
                variant={variant === "history" ? "link" : "outline-secondary"}
                size="sm"
                className={btnClass}
                style={btnStyle}
                disabled={disabled}
                onClick={() => void copy(value, key)}
            >
                {text}
            </Button>
        </OverlayTrigger>
    );

    return (
        <div
            className={
                variant === "history"
                    ? "d-inline-flex align-items-center gap-0 flex-wrap"
                    : "d-inline-flex align-items-center gap-1 ms-1 flex-wrap"
            }
            style={variant === "history" ? { gap: 2 } : undefined}
        >
            {mkBtn("pib", "ПІБ", pib, !pib)}
            {mkBtn("ipn", "ІПН", ipn, !ipn)}
            {mkBtn("iban", "IBAN", iban, !iban, iban ? `IBAN ···${iban.slice(-4)}` : "IBAN")}
            {mkBtn(
                "all",
                "всі реквізити",
                [pib, ipn, iban].filter(Boolean).join("\n"),
                !pib && !ipn && !iban,
                lastCopied === "all" ? <Check size={12} /> : <Copy size={12} />
            )}
        </div>
    );
}

/** Спосіб оплати цеху (у БД: cash | fop | per_employee). */
function workshopPaymentKindTableLabel(kind) {
    switch (kind) {
        case "fop":
            return "ФОП";
        case "per_employee":
            return "На картки";
        default:
            return "Готівка";
    }
}

function workshopPaymentKindSelectSuffix(kind) {
    switch (kind) {
        case "fop":
            return "(ФОП)";
        case "per_employee":
            return "(картки)";
        default:
            return "(готівка)";
    }
}

function normalizeWorkshopPaymentFormValue(kind) {
    return kind === "fop" || kind === "per_employee" ? kind : "cash";
}

function WorkshopPaymentKindOptions() {
    return (
        <>
            <option value="per_employee">На картки працівників (кожному окремо)</option>
            <option value="cash">Готівка цеху</option>
            <option value="fop">ФОП (реквізити на юрособу)</option>
        </>
    );
}

/** Колонки пошив/крій і видимість рядків залежно від обліку працівників та ролі. */
function payrollUiFlags(trackWorkers, workerId, workersList, separate) {
    const w = (workersList || []).find((x) => String(x._id) === String(workerId));
    const role =
        w?.role && ["seamstress", "cutter", "hardware", "other"].includes(w.role)
            ? w.role
            : "seamstress";
    const showRows = !trackWorkers || Boolean(workerId);
    if (role === "hardware") {
        return {
            showRows,
            showSewCol: false,
            showCutCol: false,
            workerPayrollRole: "hardware",
            isDailyHardware: true,
        };
    }
    const showSewCol = !trackWorkers || role === "seamstress" || role === "other";
    const showCutCol = Boolean(separate) && (!trackWorkers || role === "cutter" || role === "other");
    return {
        showRows,
        showSewCol,
        showCutCol,
        workerPayrollRole: role,
        isDailyHardware: false,
    };
}

/** Предки з прокруткою (напр. `.table-responsive`) — scroll не булькає до window */
function overflowScrollParents(el) {
    const out = [];
    let p = el?.parentElement;
    while (p) {
        const st = getComputedStyle(p);
        for (const k of ["overflow", "overflowX", "overflowY"]) {
            const v = st[k];
            if (v === "auto" || v === "scroll") {
                out.push(p);
                break;
            }
        }
        p = p.parentElement;
    }
    return out;
}

function money(n) {
    const x = Math.round((Number(n) || 0) * 100) / 100;
    return `${x.toLocaleString("uk-UA")} грн`;
}

function emptyLine() {
    return {
        label: "",
        productId: null,
        qty: 1,
        overrideSew: "",
        overrideCut: "",
        isCustomProduct: false,
        note: "",
    };
}

/** Рядок ЗП фурнітурниці: кількість днів × ставка за день (грн). За замовчуванням 5 робочих днів на тиждень. */
function hardwarePayLine(worker, preserveQty) {
    const rate =
        worker?.dailyRateUAH != null && String(worker.dailyRateUAH) !== ""
            ? String(worker.dailyRateUAH)
            : "";
    const defaultDays = 5;
    const qty =
        preserveQty !== undefined && preserveQty !== null && preserveQty !== ""
            ? preserveQty
            : defaultDays;
    return {
        label: "Робочі дні (фурнітура)",
        productId: null,
        qty,
        overrideSew: rate,
        overrideCut: "",
        isCustomProduct: true,
        payKind: "daily",
        note: "",
    };
}

function lineHasProductKeys(L) {
    const label = String(L?.label || "").trim();
    const pid = L?.productId;
    const hasPid = pid != null && pid !== "" && !Number.isNaN(Number(pid));
    return Boolean(label || hasPid);
}

function resolvePriceItemForLine(priceItems, productId, label) {
    if (productId != null && productId !== "" && !Number.isNaN(Number(productId))) {
        const pid = Number(productId);
        const byPid = priceItems.find((i) => i.productId != null && Number(i.productId) === pid);
        if (byPid) return byPid;
    }
    const nl = normWorkshopLabel(label);
    if (!nl) return null;
    return priceItems.find((i) => normWorkshopLabel(i.label) === nl) || null;
}

/** Як у lib/workshopPayrollCompute buildComputedLines — ставка за шт і сума рядка для виплати. */
function lineEffectiveMoney(L, priceItems, workshop, workerRole) {
    const separate = !!workshop?.separateCutPricing;
    const trackWorkers = !!workshop?.trackWorkers;
    const role = trackWorkers ? workerRole : null;

    if (role === "hardware") {
        const rate = Number(L.overrideSew) || 0;
        const qty = Math.max(0, Number(L.qty) || 0);
        const lineTotal = Math.round(qty * rate * 100) / 100;
        return { unitPerPiece: rate, lineTotal, matchedPrice: true };
    }

    const qty = Math.max(0, Number(L.qty) || 0);
    const resolved = resolvePriceItemForLine(priceItems, L.productId, L.label);
    let unitSew =
        L.overrideSew !== undefined && L.overrideSew !== null && L.overrideSew !== ""
            ? Number(L.overrideSew)
            : resolved
              ? Number(resolved.priceSew) || 0
              : 0;
    let unitCut =
        L.overrideCut !== undefined && L.overrideCut !== null && L.overrideCut !== ""
            ? Number(L.overrideCut)
            : resolved
              ? Number(resolved.priceCut) || 0
              : 0;
    if (!separate) unitCut = 0;
    if (Number.isNaN(unitSew)) unitSew = 0;
    if (Number.isNaN(unitCut)) unitCut = 0;

    let effSew = unitSew;
    let effCut = unitCut;
    if (role === "seamstress") effCut = 0;
    else if (role === "cutter") effSew = 0;

    const lineSew = Math.round(qty * effSew * 100) / 100;
    const lineCut = separate ? Math.round(qty * effCut * 100) / 100 : 0;
    const lineTotal = Math.round((lineSew + lineCut) * 100) / 100;
    const unitPerPiece = Math.round((effSew + effCut) * 100) / 100;
    return { unitPerPiece, lineTotal, matchedPrice: !!resolved };
}

function formatYMDLocal(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

function parseLocalYMD(s) {
    if (!s || typeof s !== "string") return new Date(NaN);
    const parts = s.split("-").map(Number);
    const [y, mo, da] = parts;
    if (!y || !mo || !da) return new Date(NaN);
    return new Date(y, mo - 1, da);
}

/** Тиждень виплат: понеділок … неділя (як у ISO). */
function mondayOfWeekContaining(ymd) {
    const d = parseLocalYMD(ymd);
    if (Number.isNaN(d.getTime())) return ymd;
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const mon = new Date(d.getFullYear(), d.getMonth(), diff);
    return formatYMDLocal(mon);
}

function sundayOfSameWeek(mondayYmd) {
    const d = parseLocalYMD(mondayYmd);
    if (Number.isNaN(d.getTime())) return mondayYmd;
    const sun = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 6);
    return formatYMDLocal(sun);
}

function snapToPayrollWeek(ymd) {
    const mon = mondayOfWeekContaining(ymd);
    return { start: mon, end: sundayOfSameWeek(mon) };
}

function weekBounds() {
    const d = new Date();
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const mon = new Date(d.getFullYear(), d.getMonth(), diff);
    mon.setHours(0, 0, 0, 0);
    const sun = new Date(mon.getFullYear(), mon.getMonth(), mon.getDate() + 6);
    sun.setHours(23, 59, 59, 999);
    return { start: mon, end: sun };
}

export default function WorkshopPayrollPage({
    urlPayrollTab = "calc",
    onPayrollTabNavigate,
}) {
    const { user } = useAuth();
    const { labelBySlug: fopLabelBySlug, slugs: fopSlugs } = useFopLabels();
    /** Історія виплат: редагування / видалення збереженого документа — admin і director */
    const isPayrollAdmin = user?.role === "admin" || user?.role === "director";

    const [localPayrollTab, setLocalPayrollTab] = useState("calc");
    const urlMode = typeof onPayrollTabNavigate === "function";
    const tab = urlMode ? urlPayrollTab || "calc" : localPayrollTab;
    const goPayrollTab = useCallback(
        (next) => {
            if (urlMode) {
                onPayrollTabNavigate(next);
            } else {
                setLocalPayrollTab(next);
            }
        },
        [urlMode, onPayrollTabNavigate]
    );
    const [workshops, setWorkshops] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const wb = useMemo(() => weekBounds(), []);
    const [periodStart, setPeriodStart] = useState(() => formatYMDLocal(wb.start));
    const [periodEnd, setPeriodEnd] = useState(() => formatYMDLocal(wb.end));
    const [workshopId, setWorkshopId] = useState("");
    const [workerId, setWorkerId] = useState("");
    const [lines, setLines] = useState([emptyLine()]);
    const [adjustments, setAdjustments] = useState([]);
    const [notes, setNotes] = useState("");
    const [preview, setPreview] = useState(null);
    const [saving, setSaving] = useState(false);
    const lastPayrollWorkerIdRef = useRef("");

    const [historyFrom, setHistoryFrom] = useState(() => formatYMDLocal(wb.start));
    const [historyTo, setHistoryTo] = useState(() => formatYMDLocal(wb.end));
    const [historyWorkshop, setHistoryWorkshop] = useState("");
    const [runs, setRuns] = useState([]);
    const [detailRun, setDetailRun] = useState(null);

    const [calcPriceItems, setCalcPriceItems] = useState([]);
    const [calcPriceLoading, setCalcPriceLoading] = useState(false);

    const selectedWorkshop = workshops.find((w) => String(w._id) === String(workshopId));

    const workshopWorkers = useMemo(() => {
        if (!workshopId || !selectedWorkshop?.trackWorkers) return [];
        return (selectedWorkshop.workers || []).filter((w) => w.active !== false);
    }, [workshopId, selectedWorkshop]);

    const loadWorkshops = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const r = await authFetch(`${API}/workshops`, { credentials: "include" });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(data.error || r.statusText);
            setWorkshops(data);
            setWorkshopId((prev) => prev || (data[0]?._id || ""));
        } catch (e) {
            setError(e.message || "Помилка");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void loadWorkshops();
    }, [loadWorkshops]);

    useEffect(() => {
        if (!workshopId || !selectedWorkshop?.trackWorkers) {
            setWorkerId("");
        }
    }, [workshopId, selectedWorkshop?.trackWorkers]);

    useEffect(() => {
        lastPayrollWorkerIdRef.current = "";
    }, [workshopId]);

    useEffect(() => {
        if (!selectedWorkshop?.trackWorkers) {
            return;
        }
        const widStr = String(workerId || "");
        if (widStr === lastPayrollWorkerIdRef.current) {
            return;
        }
        lastPayrollWorkerIdRef.current = widStr;
        if (!workerId) {
            setLines([emptyLine()]);
            setPreview(null);
            return;
        }
        const w = workshopWorkers.find((x) => String(x._id) === widStr);
        if (w?.role === "hardware") {
            setLines([hardwarePayLine(w)]);
        } else {
            setLines([emptyLine()]);
        }
        setPreview(null);
    }, [workerId, selectedWorkshop?.trackWorkers, workshopWorkers]);

    useEffect(() => {
        if (!workerId || !selectedWorkshop?.trackWorkers) {
            return;
        }
        const w = workshopWorkers.find((x) => String(x._id) === String(workerId));
        if (w?.role !== "hardware") {
            return;
        }
        const rateStr =
            w.dailyRateUAH != null && String(w.dailyRateUAH) !== "" ? String(w.dailyRateUAH) : "";
        setLines((prev) => {
            if (!prev?.length || prev[0]?.payKind !== "daily") {
                return prev;
            }
            if (prev[0].overrideSew === rateStr) {
                return prev;
            }
            return [{ ...prev[0], overrideSew: rateStr }];
        });
    }, [workshopWorkers, workerId, selectedWorkshop?.trackWorkers]);

    useEffect(() => {
        if (!workshopId) {
            setCalcPriceItems([]);
            return;
        }
        let alive = true;
        (async () => {
            setCalcPriceLoading(true);
            try {
                const r = await authFetch(`${API}/workshops/${workshopId}/prices`, { credentials: "include" });
                const data = await r.json().catch(() => []);
                if (alive && r.ok) setCalcPriceItems(Array.isArray(data) ? data : []);
            } finally {
                if (alive) setCalcPriceLoading(false);
            }
        })();
        return () => {
            alive = false;
        };
    }, [workshopId]);

    /** Підтягує прайс з сервера без спінера — зміни з вкладки «Цеха» / іншої вкладки видно без F5. */
    useEffect(() => {
        if (tab !== "calc" || !workshopId) return;
        const tick = () => {
            if (typeof document !== "undefined" && document.hidden) return;
            void (async () => {
                try {
                    const r = await authFetch(`${API}/workshops/${workshopId}/prices`, {
                        credentials: "include",
                    });
                    const data = await r.json().catch(() => []);
                    if (r.ok) setCalcPriceItems(Array.isArray(data) ? data : []);
                } catch {
                    /* ігноруємо мережеві збої — наступний інтервал */
                }
            })();
        };
        const id = setInterval(tick, 20000);
        const onVis = () => {
            if (!document.hidden) tick();
        };
        document.addEventListener("visibilitychange", onVis);
        return () => {
            clearInterval(id);
            document.removeEventListener("visibilitychange", onVis);
        };
    }, [tab, workshopId]);

    const runPreview = async () => {
        if (!workshopId) return;
        if (selectedWorkshop?.trackWorkers && !workerId) {
            setError("Оберіть працівника для розрахунку");
            return;
        }
        setError("");
        try {
            const r = await authFetch(`${API}/preview`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    workshopId,
                    workerId: selectedWorkshop?.trackWorkers ? workerId : undefined,
                    lines: lines.map((L) => ({
                        label: L.label,
                        productId: L.productId,
                        qty: L.qty,
                        overrideSew: L.overrideSew === "" ? undefined : L.overrideSew,
                        overrideCut: L.overrideCut === "" ? undefined : L.overrideCut,
                        isCustomProduct: L.isCustomProduct,
                        note: L.note,
                        ...(L.payKind === "daily" ? { payKind: "daily" } : {}),
                    })),
                    adjustments: adjustments.map((a) => ({
                        kind: a.kind,
                        amount: Number(a.amount) || 0,
                        note: a.note || "",
                        lineIndex: a.lineIndex,
                    })),
                }),
            });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(data.error || r.statusText);
            setPreview(data);
        } catch (e) {
            setError(e.message || "Помилка");
            setPreview(null);
        }
    };

    const selectedWorkerForCalc = workshopWorkers.find((w) => String(w._id) === String(workerId));
    const isHardwarePayrollWorker = selectedWorkerForCalc?.role === "hardware";
    const calcAddRowDisabled =
        !workshopId ||
        (!isHardwarePayrollWorker && !calcPriceLoading && calcPriceItems.length === 0) ||
        (selectedWorkshop?.trackWorkers && !workerId);

    const saveRun = async () => {
        if (!workshopId) return;
        if (selectedWorkshop?.trackWorkers && !workerId) {
            setError("Оберіть працівника");
            return;
        }
        setSaving(true);
        setError("");
        try {
            const r = await authFetch(`${API}/runs`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    workshopId,
                    workerId: selectedWorkshop?.trackWorkers ? workerId : null,
                    periodStart: new Date(periodStart).toISOString(),
                    periodEnd: new Date(periodEnd + "T23:59:59").toISOString(),
                    lines: lines.map((L) => ({
                        label: L.label,
                        productId: L.productId,
                        qty: L.qty,
                        overrideSew: L.overrideSew === "" ? undefined : L.overrideSew,
                        overrideCut: L.overrideCut === "" ? undefined : L.overrideCut,
                        isCustomProduct: L.isCustomProduct,
                        note: L.note,
                        ...(L.payKind === "daily" ? { payKind: "daily" } : {}),
                    })),
                    adjustments: adjustments.map((a) => ({
                        kind: a.kind,
                        amount: Number(a.amount) || 0,
                        note: a.note || "",
                        lineIndex: a.lineIndex,
                    })),
                    notes,
                }),
            });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(data.error || r.statusText);
            setPreview(null);
            setLines([emptyLine()]);
            setAdjustments([]);
            setNotes("");
            setWorkerId("");
            alert("Збережено в історію");
            goPayrollTab("history");
            void loadHistory();
        } catch (e) {
            setError(e.message || "Помилка");
        } finally {
            setSaving(false);
        }
    };

    const loadHistory = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const qs = new URLSearchParams({
                from: new Date(historyFrom).toISOString(),
                to: new Date(historyTo + "T23:59:59").toISOString(),
            });
            if (historyWorkshop) qs.set("workshopId", historyWorkshop);
            const r = await authFetch(`${API}/runs?${qs}`, { credentials: "include" });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(data.error || r.statusText);
            setRuns(data);
        } catch (e) {
            setError(e.message || "Помилка");
        } finally {
            setLoading(false);
        }
    }, [historyFrom, historyTo, historyWorkshop]);

    const mergeRunIntoLists = useCallback((updated) => {
        if (!updated?._id) return;
        const id = String(updated._id);
        setRuns((prev) => prev.map((x) => (String(x._id) === id ? { ...x, ...updated } : x)));
        setDetailRun((d) => (d && String(d._id) === id ? { ...d, ...updated } : d));
    }, []);

    const patchRunPayout = useCallback(
        async (runId, body) => {
            setError("");
            const r = await authFetch(`${API}/runs/${runId}/paid`, {
                method: "PATCH",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) {
                const msg = data.error || r.statusText;
                setError(msg);
                throw new Error(msg);
            }
            mergeRunIntoLists(data);
        },
        [mergeRunIntoLists]
    );

    const patchRunPaid = useCallback(
        async (runId, paid) => patchRunPayout(runId, { paid }),
        [patchRunPayout]
    );

    const handleCalcPeriodPick = useCallback((ymd) => {
        if (!ymd) return;
        const { start, end } = snapToPayrollWeek(ymd);
        setPeriodStart(start);
        setPeriodEnd(end);
    }, []);

    const handleHistoryFromChange = useCallback((ymd) => {
        if (!ymd) return;
        const mon = mondayOfWeekContaining(ymd);
        setHistoryFrom(mon);
        setHistoryTo((prev) => {
            if (parseLocalYMD(prev) < parseLocalYMD(mon)) return sundayOfSameWeek(mon);
            return prev;
        });
    }, []);

    const handleHistoryToChange = useCallback((ymd) => {
        if (!ymd) return;
        const mon = mondayOfWeekContaining(ymd);
        const sun = sundayOfSameWeek(mon);
        setHistoryTo(sun);
        setHistoryFrom((prev) => {
            if (parseLocalYMD(prev) > parseLocalYMD(sun)) return mon;
            return prev;
        });
    }, []);

    useEffect(() => {
        if (tab === "history") void loadHistory();
    }, [tab, loadHistory]);

    return (
        <div className="production-tab-panel tab-content workshop-payroll-module">
            <Form.Group className="d-md-none mb-3 materials-hub-tab-select">
                <Form.Label className="small text-muted mb-1">Розділ</Form.Label>
                <Form.Select
                    value={tab}
                    aria-label="Розділ зарплат цеху"
                    onChange={(e) => goPayrollTab(e.target.value)}
                >
                    <option value="calc">Розрахунок</option>
                    <option value="workshops">Цеха</option>
                    <option value="history">Історія виплат</option>
                </Form.Select>
            </Form.Group>

            <div className="materials-hub-workshop-payroll-subscroll d-none d-md-block">
                <Nav variant="tabs" className="mb-3 flex-nowrap workshop-payroll-subtabs">
                    <Nav.Item>
                        <Nav.Link active={tab === "calc"} onClick={() => goPayrollTab("calc")}>
                            Розрахунок
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link active={tab === "workshops"} onClick={() => goPayrollTab("workshops")}>
                            Цеха
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link active={tab === "history"} onClick={() => goPayrollTab("history")}>
                            Історія виплат
                        </Nav.Link>
                    </Nav.Item>
                </Nav>
            </div>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError("")}>
                    {error}
                </Alert>
            )}

            {tab === "calc" && (
                <CalcTab
                    workshops={workshops}
                    workshopId={workshopId}
                    setWorkshopId={setWorkshopId}
                    selectedWorkshop={selectedWorkshop}
                    workers={workshopWorkers}
                    workerId={workerId}
                    setWorkerId={setWorkerId}
                    periodStart={periodStart}
                    periodEnd={periodEnd}
                    onPeriodWeekPick={handleCalcPeriodPick}
                    lines={lines}
                    setLines={setLines}
                    adjustments={adjustments}
                    setAdjustments={setAdjustments}
                    notes={notes}
                    setNotes={setNotes}
                    preview={preview}
                    runPreview={runPreview}
                    saveRun={saveRun}
                    saving={saving}
                    priceItems={calcPriceItems}
                    priceItemsLoading={calcPriceLoading}
                    addRowDisabled={calcAddRowDisabled}
                    hardwarePayroll={isHardwarePayrollWorker}
                />
            )}

            {tab === "workshops" && (
                <WorkshopsTab
                    workshops={workshops}
                    onRefresh={loadWorkshops}
                    onWorkshopDeleted={(id) => {
                        if (String(workshopId) === String(id)) {
                            setWorkshopId("");
                            setWorkerId("");
                        }
                    }}
                />
            )}

            {tab === "history" && (
                <HistoryTab
                    runs={runs}
                    loading={loading}
                    onReload={loadHistory}
                    historyFrom={historyFrom}
                    onHistoryFromChange={handleHistoryFromChange}
                    historyTo={historyTo}
                    onHistoryToChange={handleHistoryToChange}
                    historyWorkshop={historyWorkshop}
                    setHistoryWorkshop={setHistoryWorkshop}
                    workshops={workshops}
                    onOpenDetail={setDetailRun}
                    onPatchRunPaid={patchRunPaid}
                    onPatchRunPayout={patchRunPayout}
                    fopSlugs={fopSlugs}
                    fopLabelBySlug={fopLabelBySlug}
                />
            )}

            <Modal show={!!detailRun} onHide={() => setDetailRun(null)} size="lg" className="workshop-payroll-module">
                <Modal.Header closeButton>
                    <Modal.Title>Деталі виплати</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {detailRun && (
                        <RunDetailBody
                            run={detailRun}
                            isAdmin={isPayrollAdmin}
                            onPatchRunPaid={patchRunPaid}
                            onPatchRunPayout={patchRunPayout}
                            fopSlugs={fopSlugs}
                            fopLabelBySlug={fopLabelBySlug}
                            onAfterMutation={() => {
                                void loadHistory();
                                setDetailRun(null);
                            }}
                        />
                    )}
                </Modal.Body>
            </Modal>
        </div>
    );
}

function ProductSuggestInput({ value, onChange, onPickProduct }) {
    const [opts, setOpts] = useState([]);
    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);
    const [menuBox, setMenuBox] = useState(null);

    useEffect(() => {
        const t = setTimeout(async () => {
            const q = String(value || "").trim();
            if (q.length < 1) {
                setOpts([]);
                return;
            }
            try {
                const r = await authFetch(
                    `${API}/product-suggest?q=${encodeURIComponent(q)}`,
                    { credentials: "include" }
                );
                const data = await r.json().catch(() => []);
                if (r.ok) setOpts(Array.isArray(data) ? data : []);
            } catch {
                setOpts([]);
            }
        }, 250);
        return () => clearTimeout(t);
    }, [value]);

    const listOpen = open && opts.length > 0;

    const syncMenuPosition = useCallback(() => {
        const el = anchorRef.current;
        if (!el || !open || opts.length < 1) {
            setMenuBox(null);
            return;
        }
        const r = el.getBoundingClientRect();
        setMenuBox({
            top: r.bottom + 4,
            left: r.left,
            width: r.width,
        });
    }, [open, opts.length]);

    useLayoutEffect(() => {
        if (!listOpen) {
            setMenuBox(null);
            return;
        }
        syncMenuPosition();
    }, [listOpen, opts, value, syncMenuPosition]);

    useEffect(() => {
        if (!listOpen) return;
        const onMove = () => syncMenuPosition();
        const el = anchorRef.current;
        const scrollEls = overflowScrollParents(el);
        for (const node of scrollEls) node.addEventListener("scroll", onMove, { passive: true });
        window.addEventListener("scroll", onMove, true);
        window.addEventListener("resize", onMove);
        return () => {
            for (const node of scrollEls) node.removeEventListener("scroll", onMove);
            window.removeEventListener("scroll", onMove, true);
            window.removeEventListener("resize", onMove);
        };
    }, [listOpen, syncMenuPosition]);

    const menuPortal =
        listOpen &&
        menuBox &&
        createPortal(
            <div
                className="bg-white border rounded shadow-sm"
                style={{
                    position: "fixed",
                    top: menuBox.top,
                    left: menuBox.left,
                    width: menuBox.width,
                    zIndex: 3000,
                    maxHeight: 200,
                    overflowY: "auto",
                }}
            >
                {opts.map((o) => (
                    <button
                        key={o.product_id}
                        type="button"
                        className="dropdown-item text-start small py-1 px-2 w-100 border-0 bg-transparent"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                            onChange(o.name);
                            onPickProduct(o.product_id);
                            setOpen(false);
                        }}
                    >
                        {o.name} <span className="text-muted">#{o.product_id}</span>
                    </button>
                ))}
            </div>,
            document.body
        );

    return (
        <div ref={anchorRef}>
            <Form.Control
                value={value}
                onChange={(e) => {
                    onChange(e.target.value);
                    onPickProduct(null);
                    setOpen(true);
                }}
                onFocus={() => setOpen(true)}
                onBlur={() => setTimeout(() => setOpen(false), 200)}
                placeholder="Назва, #id або id зі складу"
                size="sm"
            />
            {menuPortal}
        </div>
    );
}

/** Підказки лише з прайсу цеху (обхід overflow у .table-responsive — портал як у ProductSuggestInput). */
function PriceListSuggestInput({ priceItems, value, onChange, onPickProduct, disabled, workshopId }) {
    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);
    const [menuBox, setMenuBox] = useState(null);

    const opts = useMemo(() => {
        const list = Array.isArray(priceItems) ? priceItems : [];
        const raw = String(value || "").trim();
        const qq = normWorkshopLabel(value);
        const idOnly = raw.replace(/^#/, "").trim();
        const isPureId = /^\d+$/.test(idOnly);
        let filtered;
        if (!qq) {
            filtered = [...list].sort((a, b) =>
                normWorkshopLabel(a.label).localeCompare(normWorkshopLabel(b.label), "uk")
            );
        } else if (isPureId) {
            filtered = list.filter((p) => {
                if (p.productId == null) return false;
                const ps = String(p.productId);
                return ps === idOnly || ps.includes(idOnly);
            });
            filtered.sort((a, b) => {
                const sa = String(a.productId) === idOnly ? 0 : 1;
                const sb = String(b.productId) === idOnly ? 0 : 1;
                if (sa !== sb) return sa - sb;
                return normWorkshopLabel(a.label).localeCompare(normWorkshopLabel(b.label), "uk");
            });
        } else {
            const digits = qq.replace(/\D/g, "");
            filtered = list.filter((p) => {
                if (normWorkshopLabel(p.label).includes(qq)) return true;
                if (p.productId == null || !digits) return false;
                const ps = String(p.productId);
                if (digits.length >= 2) return ps.includes(digits);
                return ps === digits;
            });
        }
        return filtered.slice(0, 50);
    }, [priceItems, value]);

    const listOpen = open && !disabled && opts.length > 0;

    const syncMenuPosition = useCallback(() => {
        const el = anchorRef.current;
        if (!el || !listOpen) {
            setMenuBox(null);
            return;
        }
        const r = el.getBoundingClientRect();
        setMenuBox({
            top: r.bottom + 4,
            left: r.left,
            width: r.width,
        });
    }, [listOpen]);

    useLayoutEffect(() => {
        if (!listOpen) {
            setMenuBox(null);
            return;
        }
        syncMenuPosition();
    }, [listOpen, opts, value, syncMenuPosition]);

    useEffect(() => {
        if (!listOpen) return;
        const onMove = () => syncMenuPosition();
        const el = anchorRef.current;
        const scrollEls = overflowScrollParents(el);
        for (const node of scrollEls) node.addEventListener("scroll", onMove, { passive: true });
        window.addEventListener("scroll", onMove, true);
        window.addEventListener("resize", onMove);
        return () => {
            for (const node of scrollEls) node.removeEventListener("scroll", onMove);
            window.removeEventListener("scroll", onMove, true);
            window.removeEventListener("resize", onMove);
        };
    }, [listOpen, syncMenuPosition]);

    const menuPortal =
        listOpen &&
        menuBox &&
        createPortal(
            <div
                className="bg-white border rounded shadow-sm"
                style={{
                    position: "fixed",
                    top: menuBox.top,
                    left: menuBox.left,
                    width: menuBox.width,
                    zIndex: 3000,
                    maxHeight: 200,
                    overflowY: "auto",
                }}
            >
                {opts.map((p) => (
                    <button
                        key={p._id}
                        type="button"
                        className="dropdown-item text-start small py-1 px-2 w-100 border-0 bg-transparent"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                            onChange(p.label);
                            onPickProduct(p.productId != null ? p.productId : null);
                            setOpen(false);
                        }}
                    >
                        {p.label}{" "}
                        {p.productId != null && <span className="text-muted">#{p.productId}</span>}
                    </button>
                ))}
            </div>,
            document.body
        );

    const ph = !workshopId
        ? "Спочатку оберіть цех"
        : !priceItems?.length
          ? "Немає позицій у прайсі — додайте в «Цеха»"
          : "З прайсу: назва, #id або номер id";

    return (
        <div ref={anchorRef}>
            <Form.Control
                value={value}
                disabled={disabled}
                onChange={(e) => {
                    onChange(e.target.value);
                    onPickProduct(null);
                    setOpen(true);
                }}
                onFocus={() => setOpen(true)}
                onBlur={() => setTimeout(() => setOpen(false), 200)}
                placeholder={ph}
                size="sm"
            />
            {menuPortal}
        </div>
    );
}

function CalcTab({
    workshops,
    workshopId,
    setWorkshopId,
    selectedWorkshop,
    workers,
    workerId,
    setWorkerId,
    periodStart,
    periodEnd,
    onPeriodWeekPick,
    lines,
    setLines,
    adjustments,
    setAdjustments,
    notes,
    setNotes,
    preview,
    runPreview,
    saveRun,
    saving,
    priceItems,
    priceItemsLoading,
    addRowDisabled,
    hardwarePayroll = false,
}) {
    const [showDatePicker, setShowDatePicker] = useState(false);

    const fmtShortUk = (ymd) => {
        if (!ymd) return "—";
        const d = new Date(ymd + "T12:00:00");
        return d.toLocaleDateString("uk-UA", { day: "numeric", month: "short" }).replace(".", "");
    };
    const periodLabel = `${fmtShortUk(periodStart)} — ${fmtShortUk(periodEnd)}`;

    const handlePickAndClose = useCallback(
        (ymd) => {
            onPeriodWeekPick(ymd);
            setShowDatePicker(false);
        },
        [onPeriodWeekPick]
    );

    const separate = selectedWorkshop?.separateCutPricing;
    const { showRows, showSewCol, showCutCol, workerPayrollRole } = payrollUiFlags(
        !!selectedWorkshop?.trackWorkers,
        workerId,
        workers,
        !!separate
    );
    const roleForLineMoney = selectedWorkshop?.trackWorkers ? workerPayrollRole : null;

    const updateLine = (i, patch) => {
        setLines((prev) => prev.map((L, j) => (j === i ? { ...L, ...patch } : L)));
    };

    const linesSumTotal = useMemo(() => {
        let s = 0;
        for (const L of lines) {
            const lm = lineEffectiveMoney(L, priceItems, selectedWorkshop, roleForLineMoney);
            s += Number(lm.lineTotal) || 0;
        }
        return Math.round(s * 100) / 100;
    }, [lines, priceItems, selectedWorkshop, roleForLineMoney]);

    return (
        <div>
            <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
                <div className="d-flex align-items-center gap-1">
                    <span className="text-muted small">Період</span>
                    {showDatePicker ? (
                        <AppDateField value={periodStart} onChange={handlePickAndClose} size="sm" />
                    ) : (
                        <>
                            <span className="fw-medium">{periodLabel}</span>
                            <Button
                                variant="link"
                                size="sm"
                                className="p-1 text-decoration-none border-0 shadow-none"
                                style={{ color: "#212529" }}
                                title="Змінити період"
                                onClick={() => setShowDatePicker(true)}
                            >
                                <Pencil size={18} aria-hidden />
                            </Button>
                        </>
                    )}
                </div>
                <Form.Select
                    size="sm"
                    style={{ width: "auto" }}
                    value={workshopId}
                    onChange={(e) => setWorkshopId(e.target.value)}
                >
                    <option value="">— Цех —</option>
                    {workshops.map((w) => (
                        <option key={w._id} value={w._id}>
                            {w.name} {workshopPaymentKindSelectSuffix(w.paymentKind)}
                        </option>
                    ))}
                </Form.Select>
                {selectedWorkshop?.trackWorkers && (
                    <Form.Select
                        size="sm"
                        style={{ width: "auto" }}
                        value={workerId}
                        onChange={(e) => setWorkerId(e.target.value)}
                    >
                        <option value="">— Працівник —</option>
                        {workers.map((w) => (
                            <option key={w._id} value={w._id}>
                                {w.fullName} ({workerRoleLabelUk(w.role)})
                            </option>
                        ))}
                    </Form.Select>
                )}
            </div>

            {selectedWorkshop?.trackWorkers && !workerId && (
                <p className="small text-muted mb-2">
                    Оберіть працівника в полі зверху — після цього з’являться товари, штрафи/доплати та кнопка збереження
                    виплати.
                </p>
            )}

            {showRows && (
                <>
                    {hardwarePayroll ? (
                        <>
                            <div className="mb-2">
                                <strong>Оплата за робочі дні</strong>
                                <p className="small text-muted mb-0">
                                    Вкажіть кількість днів за тиждень. Ставка (грн/день) з картки працівника на вкладці
                                    «Цеха»; тут її можна змінити лише для цього розрахунку.
                                </p>
                            </div>
                            <Table responsive bordered size="sm" className="align-middle">
                                <thead className="table-light">
                                    <tr>
                                        <th>Опис</th>
                                        <th style={{ width: 100 }}>Днів</th>
                                        <th style={{ width: 120 }}>Грн за день</th>
                                        <th style={{ width: 110 }} className="text-end">
                                            Сума
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {lines.map((L, i) => {
                                        const lm = lineEffectiveMoney(
                                            L,
                                            priceItems,
                                            selectedWorkshop,
                                            "hardware"
                                        );
                                        return (
                                            <tr key={i}>
                                                <td className="small">{L.label}</td>
                                                <td>
                                                    <Form.Control
                                                        type="number"
                                                        min={0}
                                                        step={0.5}
                                                        size="sm"
                                                        value={L.qty}
                                                        onChange={(e) => updateLine(i, { qty: e.target.value })}
                                                    />
                                                </td>
                                                <td>
                                                    <Form.Control
                                                        type="number"
                                                        min={0}
                                                        step={1}
                                                        size="sm"
                                                        value={L.overrideSew}
                                                        onChange={(e) =>
                                                            updateLine(i, { overrideSew: e.target.value })
                                                        }
                                                    />
                                                </td>
                                                <td className="text-end small fw-medium">{money(lm.lineTotal)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot className="table-light">
                                    <tr>
                                        <td colSpan={3} className="fw-semibold text-end small">
                                            Усього
                                        </td>
                                        <td className="text-end small fw-semibold text-nowrap">
                                            {money(linesSumTotal)}
                                        </td>
                                    </tr>
                                </tfoot>
                            </Table>
                        </>
                    ) : (
                        <>
                            {workshopId && !priceItemsLoading && priceItems.length === 0 && (
                                <p className="small text-warning mb-2">
                                    У цього цеху порожній прайс. Відкрийте вкладку «Цеха» → «Прайс» біля цеху → додайте
                                    хоча б один товар з ціною.
                                </p>
                            )}

                            <Table responsive bordered size="sm" className="align-middle">
                                <thead className="table-light">
                                    <tr>
                                        <th>Товар</th>
                                        <th style={{ width: 70 }}>К-сть</th>
                                        {showSewCol && <th style={{ width: 100, lineHeight: 1.1 }}>Ціна пошив<br /><span className="text-muted fw-normal" style={{ fontSize: "0.72em" }}>з прайсу</span></th>}
                                        {showCutCol && <th style={{ width: 100, lineHeight: 1.1 }}>Ціна крій<br /><span className="text-muted fw-normal" style={{ fontSize: "0.72em" }}>з прайсу</span></th>}
                                        <th style={{ width: 96 }} className="text-end">
                                            За 1 шт
                                        </th>
                                        <th style={{ width: 104 }} className="text-end">
                                            Сума
                                        </th>
                                        <th style={{ width: 40 }} />
                                    </tr>
                                </thead>
                                <tbody>
                                    {lines.map((L, i) => {
                                        const lm = lineEffectiveMoney(
                                            L,
                                            priceItems,
                                            selectedWorkshop,
                                            roleForLineMoney
                                        );
                                        const showMoneyDash = !lineHasProductKeys(L);
                                        const resolved = resolvePriceItemForLine(priceItems, L.productId, L.label);
                                        return (
                                            <tr key={i}>
                                                <td>
                                                    <PriceListSuggestInput
                                                        workshopId={workshopId}
                                                        priceItems={priceItems}
                                                        disabled={!workshopId || priceItems.length === 0}
                                                        value={L.label}
                                                        onChange={(v) => updateLine(i, { label: v })}
                                                        onPickProduct={(pid) => updateLine(i, { productId: pid })}
                                                    />
                                                </td>
                                                <td>
                                                    <Form.Control
                                                        type="number"
                                                        min={0}
                                                        size="sm"
                                                        value={L.qty}
                                                        onChange={(e) => updateLine(i, { qty: e.target.value })}
                                                    />
                                                </td>
                                                {showSewCol && (
                                                    <td>
                                                        <Form.Control
                                                            type="number"
                                                            min={0}
                                                            step="0.01"
                                                            size="sm"
                                                            value={
                                                                L.overrideSew !== ""
                                                                    ? L.overrideSew
                                                                    : resolved
                                                                      ? String(resolved.priceSew ?? "")
                                                                      : ""
                                                            }
                                                            onChange={(e) =>
                                                                updateLine(i, { overrideSew: e.target.value })
                                                            }
                                                        />
                                                    </td>
                                                )}
                                                {showCutCol && (
                                                    <td>
                                                        <Form.Control
                                                            type="number"
                                                            min={0}
                                                            step="0.01"
                                                            size="sm"
                                                            value={
                                                                L.overrideCut !== ""
                                                                    ? L.overrideCut
                                                                    : resolved
                                                                      ? String(resolved.priceCut ?? "")
                                                                      : ""
                                                            }
                                                            onChange={(e) =>
                                                                updateLine(i, { overrideCut: e.target.value })
                                                            }
                                                        />
                                                    </td>
                                                )}
                                                <td className="text-end small text-muted text-nowrap">
                                                    {showMoneyDash ? "—" : money(lm.unitPerPiece)}
                                                </td>
                                                <td className="text-end small text-nowrap">
                                                    {showMoneyDash ? "—" : money(lm.lineTotal)}
                                                </td>
                                                <td>
                                                    <Button
                                                        variant="link"
                                                        className="text-danger p-0"
                                                        onClick={() =>
                                                            setLines((p) => p.filter((_, j) => j !== i))
                                                        }
                                                    >
                                                        <Trash2 size={15} aria-hidden />
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot className="table-light">
                                    <tr>
                                        <td style={{ borderTop: "none", textAlign: "left" }}>
                                            <Button
                                                size="sm"
                                                variant="outline-secondary"
                                                disabled={addRowDisabled}
                                                title={
                                                    addRowDisabled
                                                        ? "Потрібні цех, прайс, за наявності обліку — обраний працівник"
                                                        : "Додати товар"
                                                }
                                                onClick={() => setLines((p) => [...p, emptyLine()])}
                                            >
                                                +
                                            </Button>
                                        </td>
                                        <td colSpan={showSewCol + showCutCol + 1} style={{ borderTop: "none" }} />
                                        <td className="fw-semibold text-end small">Усього</td>
                                        <td className="text-end small fw-semibold text-nowrap">
                                            {money(linesSumTotal)}
                                        </td>
                                        <td />
                                    </tr>
                                </tfoot>
                            </Table>
                        </>
                    )}

                    <div className="d-flex align-items-center gap-2 mb-2">
                        <strong className="small">Штрафи / доплати</strong>
                        <Button
                            size="sm"
                            variant="outline-secondary"
                            title="Додати штраф або доплату"
                            onClick={() =>
                                setAdjustments((p) => [...p, { kind: "bonus", amount: 0, note: "", lineIndex: null }])
                            }
                        >
                            +
                        </Button>
                    </div>
                    {adjustments.map((a, i) => (
                        <div key={i} className="d-flex gap-2 align-items-center mb-1 flex-nowrap">
                            <Form.Select
                                size="sm"
                                style={{ width: 120, flexShrink: 0 }}
                                value={a.kind}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setAdjustments((p) => p.map((x, j) => (j === i ? { ...x, kind: v } : x)));
                                }}
                            >
                                <option value="bonus">Доплата</option>
                                <option value="penalty">Штраф</option>
                            </Form.Select>
                            <Form.Control
                                type="number"
                                size="sm"
                                style={{ width: 100, flexShrink: 0 }}
                                value={a.amount}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setAdjustments((p) => p.map((x, j) => (j === i ? { ...x, amount: v } : x)));
                                }}
                            />
                            <Form.Control
                                size="sm"
                                placeholder="Коментар"
                                value={a.note}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setAdjustments((p) => p.map((x, j) => (j === i ? { ...x, note: v } : x)));
                                }}
                                style={{ flexGrow: 1 }}
                            />
                            <Button
                                variant="link"
                                className="text-danger p-0 flex-shrink-0"
                                onClick={() => setAdjustments((p) => p.filter((_, j) => j !== i))}
                            >
                                <Trash2 size={15} aria-hidden />
                            </Button>
                        </div>
                    ))}

                    <Form.Group className="mb-3">
                        <Form.Label className="small text-muted">Загальна нотатка до розрахунку</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            size="sm"
                        />
                    </Form.Group>

                    <div className="d-flex flex-wrap gap-2 mb-3">
                        <Button variant="outline-primary" onClick={() => void runPreview()}>
                            Перерахувати
                        </Button>
                        <Button variant="success" disabled={saving} onClick={() => void saveRun()}>
                            {saving ? <Spinner size="sm" animation="border" /> : "Зберегти виплату"}
                        </Button>
                    </div>

                    {preview?.computed && (
                        <div className="border rounded p-3 bg-light">
                            <h6 className="mb-2">Попередній підсумок</h6>
                            <Table size="sm" className="mb-2">
                                <thead>
                                    <tr>
                                        <th>Товар</th>
                                        <th>К-сть</th>
                                        {hardwarePayroll ? (
                                            <th className="text-end">Грн за день</th>
                                        ) : (
                                            (showSewCol || showCutCol) && (
                                                <th className="text-end">За 1 шт</th>
                                            )
                                        )}
                                        <th className="text-end">Усього</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {preview.computed.map((r, i) => {
                                        const uS = Number(r.unitPriceSew) || 0;
                                        const uC = Number(r.unitPriceCut) || 0;
                                        const uP = Math.round((uS + uC) * 100) / 100;
                                        return (
                                            <tr key={i}>
                                                <td>
                                                    {r.label}{" "}
                                                    {!r.matchedPrice && (
                                                        <Badge bg="warning" text="dark" className="ms-1">
                                                            нема в прайсі
                                                        </Badge>
                                                    )}
                                                </td>
                                                <td>{r.qty}</td>
                                                {hardwarePayroll ? (
                                                    <td className="text-end small">{money(uS)}</td>
                                                ) : (
                                                    (showSewCol || showCutCol) && (
                                                        <td className="text-end small text-muted">
                                                            {uP > 0 ? money(uP) : "—"}
                                                        </td>
                                                    )
                                                )}
                                                <td className="text-end fw-medium">{money(r.lineTotal)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                            <div className="small">
                                <div>
                                    Разом по рядках:{" "}
                                    {money(
                                        (Number(preview.totals.linesSew) || 0) +
                                            (Number(preview.totals.linesCut) || 0)
                                    )}
                                </div>
                                <div>Коригування: {money(preview.totals.adjustments)}</div>
                                <strong>До виплати: {money(preview.totals.grand)}</strong>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function WorkshopsTab({ workshops, onRefresh, onWorkshopDeleted }) {
    const [sel, setSel] = useState("");
    const [prices, setPrices] = useState([]);
    const [newPrice, setNewPrice] = useState({ label: "", productId: "", priceSew: "", priceCut: "" });
    const [workshopModal, setWorkshopModal] = useState(null);

    const [wmName, setWmName] = useState("");
    const [wmPayment, setWmPayment] = useState("cash");
    const [wmTrack, setWmTrack] = useState(false);
    const [wmCut, setWmCut] = useState(false);
    const [wmSort, setWmSort] = useState(0);

    const selected = workshops.find((w) => String(w._id) === String(sel));

    const workersList = useMemo(() => {
        if (!selected?.trackWorkers) return [];
        return Array.isArray(selected.workers) ? selected.workers : [];
    }, [selected]);

    const openCreateWorkshop = () => {
        setWmName("");
        setWmPayment("cash");
        setWmTrack(false);
        setWmCut(false);
        setWmSort(0);
        setWorkshopModal({ mode: "create" });
    };

    const openEditWorkshop = (w) => {
        setWmName(w.name || "");
        setWmPayment(normalizeWorkshopPaymentFormValue(w.paymentKind));
        setWmTrack(!!w.trackWorkers);
        setWmCut(!!w.separateCutPricing);
        setWmSort(Number(w.sortOrder) || 0);
        setWorkshopModal({ mode: "edit", id: w._id });
    };

    const saveWorkshopModal = async () => {
        const body = {
            name: wmName.trim() || "Цех",
            paymentKind: wmPayment,
            trackWorkers: wmTrack,
            separateCutPricing: wmCut,
            sortOrder: Number(wmSort) || 0,
        };
        if (workshopModal?.mode === "create") {
            const r = await authFetch(`${API}/workshops`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            const data = await r.json().catch(() => ({}));
            if (r.ok && data._id) setSel(String(data._id));
        } else if (workshopModal?.mode === "edit" && workshopModal.id) {
            await authFetch(`${API}/workshops/${workshopModal.id}`, {
                method: "PATCH",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
        }
        setWorkshopModal(null);
        await onRefresh();
    };

    const deleteWorkshop = async (w) => {
        if (!window.confirm(`Прибрати цех «${w.name}» зі списку? Прайс і записи в історії залишаться в базі.`)) {
            return;
        }
        const r = await authFetch(`${API}/workshops/${w._id}`, { method: "DELETE", credentials: "include" });
        if (r.ok) {
            if (String(sel) === String(w._id)) setSel("");
            onWorkshopDeleted?.(w._id);
            await onRefresh();
        }
    };

    const loadPrices = useCallback(async () => {
        if (!sel) {
            setPrices([]);
            return;
        }
        const r = await authFetch(`${API}/workshops/${sel}/prices`, { credentials: "include" });
        const data = await r.json().catch(() => []);
        if (r.ok) setPrices(data);
    }, [sel]);

    useEffect(() => {
        void loadPrices();
    }, [loadPrices]);

    const patchWorkshopQuick = async (patch) => {
        if (!sel) return;
        const r = await authFetch(`${API}/workshops/${sel}`, {
            method: "PATCH",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(patch),
        });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) {
            alert(data.error || "Не вдалося зберегти налаштування");
            return;
        }
        await onRefresh();
    };

    const addPrice = async () => {
        if (!sel || !String(newPrice.label || "").trim()) {
            alert("Вкажіть назву товару");
            return;
        }
        const r = await authFetch(`${API}/workshops/${sel}/prices`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                label: newPrice.label.trim(),
                productId: newPrice.productId === "" ? null : Number(newPrice.productId),
                priceSew: Number(newPrice.priceSew) || 0,
                priceCut: Number(newPrice.priceCut) || 0,
            }),
        });
        if (!r.ok) {
            const err = await r.json().catch(() => ({}));
            alert(err.error || "Помилка");
            return;
        }
        setNewPrice({ label: "", productId: "", priceSew: "", priceCut: "" });
        await loadPrices();
    };

    return (
        <div>
            <p className="text-muted small">
                Тут ви ведете <strong>цехи</strong> та їх <strong>прайс</strong> (ціна пошиву й крою за 1 шт.). Натисніть{" "}
                <strong>Прайс</strong> в рядку цеху — відкриється редагування. У вкладці <strong>Розрахунок</strong> можна
                вибрати лише товари з прайсу цього цеху.
            </p>

            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                <h6 className="mb-0">Список цехів</h6>
                <Button size="sm" onClick={openCreateWorkshop}>
                    + Додати цех
                </Button>
            </div>

            <Table responsive bordered size="sm" className="align-middle mb-4">
                <thead className="table-light">
                    <tr>
                        <th>Назва</th>
                        <th>Спосіб оплати</th>
                        <th>Облік працівників</th>
                        <th>Роздільно пошив і крій</th>
                        <th style={{ minWidth: 220 }}>Дії</th>
                    </tr>
                </thead>
                <tbody>
                    {workshops.length === 0 && (
                        <tr>
                            <td colSpan={5} className="text-muted text-center py-3">
                                Немає цехів — натисніть «Додати цех»
                            </td>
                        </tr>
                    )}
                    {workshops.map((w) => (
                        <tr key={w._id}>
                            <td>{w.name}</td>
                            <td>{workshopPaymentKindTableLabel(w.paymentKind)}</td>
                            <td>{w.trackWorkers ? "так" : "ні"}</td>
                            <td>{w.separateCutPricing ? "так" : "ні"}</td>
                            <td>
                                <Button size="sm" variant="outline-secondary" className="me-1 py-0" onClick={() => setSel(w._id)}>
                                    Прайс
                                </Button>
                                <Button size="sm" variant="outline-primary" className="me-1 py-0" onClick={() => openEditWorkshop(w)}>
                                    Змінити
                                </Button>
                                <Button size="sm" variant="outline-danger" className="py-0" onClick={() => void deleteWorkshop(w)}>
                                    Видалити
                                </Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>

            {!sel ? (
                <p className="text-muted small">
                    Оберіть цех: натисніть <strong>Прайс</strong> в рядку — з’явиться прайс і (за потреби) список
                    працівників.
                </p>
            ) : (
                <div className="border rounded p-3 bg-light">
                    <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
                        <div>
                            <h5 className="mb-1">{selected?.name}</h5>
                            <div className="small text-muted">ID: {sel}</div>
                        </div>
                        <div className="d-flex flex-wrap gap-2 align-items-center">
                            <CheckboxHolder
                                className="mb-0"
                                formClassName="d-flex flex-wrap gap-3 align-items-center mb-0"
                            >
                                <AppCheckbox
                                    id={`wp-track-${sel}`}
                                    label="Окремо працівники"
                                    checked={!!selected?.trackWorkers}
                                    onChange={(e) => {
                                        const v = e.target.checked;
                                        void patchWorkshopQuick({ trackWorkers: v });
                                    }}
                                />
                                <AppCheckbox
                                    id={`wp-cut-${sel}`}
                                    className="wp-cut-pricing-check"
                                    label="Роздільно пошив і крій"
                                    checked={!!selected?.separateCutPricing}
                                    onChange={(e) => {
                                        const v = e.target.checked;
                                        void patchWorkshopQuick({ separateCutPricing: v });
                                    }}
                                />
                            </CheckboxHolder>
                            <Form.Select
                                size="sm"
                                style={{ minWidth: 220 }}
                                value={normalizeWorkshopPaymentFormValue(selected?.paymentKind)}
                                onChange={(e) => void patchWorkshopQuick({ paymentKind: e.target.value })}
                            >
                                <WorkshopPaymentKindOptions />
                            </Form.Select>
                        </div>
                    </div>

                    <h6 className="small text-uppercase text-muted mb-2">Прайс</h6>
                    <p className="small text-muted mb-2">
                        У таблиці вкажіть товар (є підказки зі складу), ставки <strong>Пошив</strong> і{" "}
                        <strong>Крій</strong> у грн за 1 шт. Новий рядок — форма під таблицею, потім «Створити».
                    </p>
                    <Table size="sm" bordered responsive className="bg-white">
                        <thead className="table-light">
                            <tr>
                                <th style={{ minWidth: 220 }}>Товар</th>
                                <th style={{ width: 100 }}>product_id</th>
                                <th style={{ width: 100 }}>Пошив, грн</th>
                                <th style={{ width: 100 }}>Крій, грн</th>
                                <th style={{ width: 130 }}>Дії</th>
                            </tr>
                        </thead>
                        <tbody>
                            {prices.map((p) => (
                                <PriceRow key={p._id} p={p} onSaved={loadPrices} />
                            ))}
                        </tbody>
                    </Table>

                    <div className="row g-2 align-items-end mb-4">
                        <div className="col-md-4">
                            <Form.Label className="small text-muted mb-0">Новий рядок прайсу</Form.Label>
                            <ProductSuggestInput
                                value={newPrice.label}
                                onChange={(v) => setNewPrice((x) => ({ ...x, label: v }))}
                                onPickProduct={(pid) =>
                                    setNewPrice((x) => ({ ...x, productId: pid != null ? String(pid) : x.productId }))
                                }
                            />
                        </div>
                        <div className="col-md-2">
                            <Form.Label className="small text-muted mb-0">product_id</Form.Label>
                            <Form.Control
                                size="sm"
                                placeholder="опційно"
                                value={newPrice.productId}
                                onChange={(e) => setNewPrice((x) => ({ ...x, productId: e.target.value }))}
                            />
                        </div>
                        <div className="col-md-2">
                            <Form.Label className="small text-muted mb-0">Пошив</Form.Label>
                            <Form.Control
                                size="sm"
                                type="number"
                                min={0}
                                step="0.01"
                                value={newPrice.priceSew}
                                onChange={(e) => setNewPrice((x) => ({ ...x, priceSew: e.target.value }))}
                            />
                        </div>
                        <div className="col-md-2">
                            <Form.Label className="small text-muted mb-0">Крій</Form.Label>
                            <Form.Control
                                size="sm"
                                type="number"
                                min={0}
                                step="0.01"
                                value={newPrice.priceCut}
                                onChange={(e) => setNewPrice((x) => ({ ...x, priceCut: e.target.value }))}
                            />
                        </div>
                        <div className="col-md-2">
                            <Button size="sm" className="w-100" onClick={() => void addPrice()}>
                                Створити
                            </Button>
                        </div>
                    </div>

                    {selected?.trackWorkers && (
                        <WorkshopWorkersCrud workshopId={sel} workers={workersList} onChanged={onRefresh} />
                    )}
                </div>
            )}

            <Modal show={!!workshopModal} onHide={() => setWorkshopModal(null)} centered className="workshop-payroll-module">
                <Modal.Header closeButton>
                    <Modal.Title>{workshopModal?.mode === "create" ? "Новий цех" : "Редагувати цех"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-2">
                        <Form.Label className="small">Назва</Form.Label>
                        <Form.Control value={wmName} onChange={(e) => setWmName(e.target.value)} />
                    </Form.Group>
                    <Form.Group className="mb-2">
                        <Form.Label className="small">Спосіб оплати</Form.Label>
                        <Form.Select value={wmPayment} onChange={(e) => setWmPayment(e.target.value)}>
                            <WorkshopPaymentKindOptions />
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-2">
                        <Form.Label className="small">Порядок у списку</Form.Label>
                        <Form.Control
                            type="number"
                            value={wmSort}
                            onChange={(e) => setWmSort(e.target.value)}
                        />
                    </Form.Group>
                    <CheckboxHolder className="mb-0">
                        <AppCheckbox
                            id="wp-modal-track"
                            className="mb-2"
                            label="Окремо працівники (наш цех)"
                            checked={wmTrack}
                            onChange={(e) => setWmTrack(e.target.checked)}
                        />
                        <AppCheckbox
                            id="wp-modal-cut"
                            className="mb-0 wp-cut-pricing-check"
                            label="Роздільно пошив і крій"
                            checked={wmCut}
                            onChange={(e) => setWmCut(e.target.checked)}
                        />
                    </CheckboxHolder>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" size="sm" onClick={() => setWorkshopModal(null)}>
                        Скасувати
                    </Button>
                    <Button size="sm" onClick={() => void saveWorkshopModal()}>
                        Зберегти
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

function PriceRow({ p, onSaved }) {
    const [label, setLabel] = useState(p.label);
    const [productId, setProductId] = useState(p.productId != null ? String(p.productId) : "");
    const [priceSew, setPriceSew] = useState(String(p.priceSew));
    const [priceCut, setPriceCut] = useState(String(p.priceCut ?? 0));

    useEffect(() => {
        setLabel(p.label);
        setProductId(p.productId != null ? String(p.productId) : "");
        setPriceSew(String(p.priceSew));
        setPriceCut(String(p.priceCut ?? 0));
    }, [p._id, p.label, p.productId, p.priceSew, p.priceCut]);

    const save = async () => {
        await authFetch(`${API}/prices/${p._id}`, {
            method: "PATCH",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                label: label.trim() || "Товар",
                productId: productId === "" ? null : Number(productId),
                priceSew: Number(priceSew) || 0,
                priceCut: Number(priceCut) || 0,
            }),
        });
        await onSaved();
    };

    const del = async () => {
        if (!window.confirm("Видалити позицію прайсу?")) return;
        await authFetch(`${API}/prices/${p._id}`, { method: "DELETE", credentials: "include" });
        await onSaved();
    };

    return (
        <tr>
            <td>
                <ProductSuggestInput
                    value={label}
                    onChange={setLabel}
                    onPickProduct={(pid) => setProductId(pid != null ? String(pid) : "")}
                />
            </td>
            <td>
                <Form.Control
                    size="sm"
                    placeholder="—"
                    value={productId}
                    onChange={(e) => setProductId(e.target.value)}
                />
            </td>
            <td>
                <Form.Control
                    size="sm"
                    type="number"
                    min={0}
                    step="0.01"
                    value={priceSew}
                    onChange={(e) => setPriceSew(e.target.value)}
                />
            </td>
            <td>
                <Form.Control
                    size="sm"
                    type="number"
                    min={0}
                    step="0.01"
                    value={priceCut}
                    onChange={(e) => setPriceCut(e.target.value)}
                />
            </td>
            <td>
                <Button size="sm" variant="primary" className="me-1 py-0" onClick={() => void save()}>
                    Зберегти
                </Button>
                <Button size="sm" variant="outline-danger" className="py-0" onClick={() => void del()}>
                    Видалити
                </Button>
            </td>
        </tr>
    );
}

const PAYROLL_HISTORY_ROLE_ORDER = ["seamstress", "cutter", "hardware", "other", "_none"];

/** Підсумок grand по ролях для списку runs (заголовки цехів у акордеоні). */
function workshopRollupFromRuns(runList) {
    const roleMap = new Map();
    let total = 0;
    for (const r of runList || []) {
        const g = Number(r.totals?.grand);
        const add = Number.isFinite(g) ? g : 0;
        total += add;
        const wid = r.workerId;
        const roleKey =
            wid && typeof wid === "object" && wid.role ? wid.role : "_none";
        roleMap.set(roleKey, (roleMap.get(roleKey) || 0) + add);
    }
    const orderedParts = [];
    for (const key of PAYROLL_HISTORY_ROLE_ORDER) {
        const raw = roleMap.get(key);
        if (raw == null || raw === 0) continue;
        const sum = Math.round(Number(raw) * 100) / 100;
        orderedParts.push({ role: key, sum });
    }
    for (const [key, raw] of roleMap) {
        if (PAYROLL_HISTORY_ROLE_ORDER.includes(key)) continue;
        if (raw == null || raw === 0) continue;
        const sum = Math.round(Number(raw) * 100) / 100;
        orderedParts.push({ role: key, sum });
    }
    return {
        total: Math.round(total * 100) / 100,
        parts: orderedParts,
    };
}

function HistoryTab({
    runs,
    loading,
    onReload,
    historyFrom,
    onHistoryFromChange,
    historyTo,
    onHistoryToChange,
    historyWorkshop,
    setHistoryWorkshop,
    workshops,
    onOpenDetail,
    onPatchRunPaid,
    onPatchRunPayout,
    fopSlugs,
    fopLabelBySlug,
}) {
    const [paidBusyId, setPaidBusyId] = useState(null);
    const [fopBusyId, setFopBusyId] = useState(null);

    const historyAccordionData = useMemo(() => {
        const byPeriod = new Map();
        for (const r of runs || []) {
            const t0 = new Date(r.periodStart).getTime();
            const t1 = new Date(r.periodEnd).getTime();
            const periodKey = `${t0}|${t1}`;
            const periodLabel = `${new Date(r.periodStart).toLocaleDateString("uk-UA")} — ${new Date(r.periodEnd).toLocaleDateString("uk-UA")}`;
            if (!byPeriod.has(periodKey)) {
                byPeriod.set(periodKey, {
                    periodKey,
                    periodLabel,
                    periodStartMs: t0,
                    runs: [],
                });
            }
            byPeriod.get(periodKey).runs.push(r);
        }
        const periods = [];
        for (const block of byPeriod.values()) {
            const wsMap = new Map();
            for (const r of block.runs) {
                const id = r.workshopId?._id != null ? String(r.workshopId._id) : "";
                const workshopKey = id || `name:${String(r.workshopId?.name || "—")}`;
                if (!wsMap.has(workshopKey)) {
                    wsMap.set(workshopKey, {
                        workshopKey,
                        workshopName: r.workshopId?.name || "—",
                        runs: [],
                    });
                }
                wsMap.get(workshopKey).runs.push(r);
            }
            const workshops = [...wsMap.values()]
                .map((w) => {
                    const { total, parts } = workshopRollupFromRuns(w.runs);
                    const sortedRuns = [...w.runs].sort((a, b) =>
                        String(a.workerId?.fullName || "").localeCompare(
                            String(b.workerId?.fullName || ""),
                            "uk"
                        )
                    );
                    return { ...w, total, parts, runs: sortedRuns };
                })
                .sort((a, b) =>
                    a.workshopName.localeCompare(b.workshopName, "uk")
                );
            const periodTotal = Math.round(
                workshops.reduce((s, w) => s + (Number(w.total) || 0), 0) * 100
            ) / 100;
            periods.push({
                periodKey: block.periodKey,
                periodLabel: block.periodLabel,
                periodStartMs: block.periodStartMs,
                total: periodTotal,
                workshops,
            });
        }
        periods.sort((a, b) => b.periodStartMs - a.periodStartMs);
        return periods;
    }, [runs]);

    return (
        <div>
            <div className="d-flex flex-wrap gap-2 align-items-end mb-3">
                <div>
                    <Form.Label className="small text-muted mb-0">З (пн тижня)</Form.Label>
                    <AppDateField value={historyFrom} onChange={onHistoryFromChange} size="sm" />
                </div>
                <div>
                    <Form.Label className="small text-muted mb-0">По (нд тижня)</Form.Label>
                    <AppDateField value={historyTo} onChange={onHistoryToChange} size="sm" />
                </div>
                <div>
                    <Form.Label className="small text-muted mb-0">Цех</Form.Label>
                    <Form.Select
                        size="sm"
                        value={historyWorkshop}
                        onChange={(e) => setHistoryWorkshop(e.target.value)}
                        style={{ minWidth: 160 }}
                    >
                        <option value="">Усі</option>
                        {workshops.map((w) => (
                            <option key={w._id} value={w._id}>
                                {w.name}
                            </option>
                        ))}
                    </Form.Select>
                </div>
                <Button size="sm" onClick={() => void onReload()}>
                    Оновити
                </Button>
            </div>
            {loading ? (
                <Spinner animation="border" size="sm" />
            ) : runs.length === 0 ? (
                <p className="text-muted small mb-0">Немає записів за обрані умови.</p>
            ) : (
                <Accordion
                    defaultActiveKey={historyAccordionData.length ? "hist-p-0" : undefined}
                    className="workshop-payroll-history-accordion"
                >
                    {historyAccordionData.map((period, pIdx) => {
                        const periodWorkshopSummary =
                            period.workshops.length > 0
                                ? period.workshops
                                      .map((w) => `${w.workshopName} ${money(w.total)}`)
                                      .join(", ")
                                : "";
                        return (
                        <Accordion.Item key={period.periodKey} eventKey={`hist-p-${pIdx}`}>
                            <Accordion.Header>
                                <div className="d-flex flex-wrap align-items-baseline justify-content-between w-100 gap-2 pe-2">
                                    <span className="text-start">
                                        <span className="fw-semibold">{period.periodLabel}</span>
                                        {periodWorkshopSummary ? (
                                            <span className="text-muted small fw-normal ms-1">
                                                ({periodWorkshopSummary})
                                            </span>
                                        ) : null}
                                    </span>
                                    <span className="text-nowrap fw-semibold">{money(period.total)}</span>
                                </div>
                            </Accordion.Header>
                            <Accordion.Body className="p-2 pt-1">
                                <Accordion flush>
                                    {period.workshops.map((ws, wIdx) => {
                                        const roleLine =
                                            ws.parts.length > 0
                                                ? ws.parts
                                                      .map(
                                                          (p) =>
                                                              `${historyPayrollRoleGroupLabelUk(p.role)} ${money(p.sum)}`
                                                      )
                                                      .join(" · ")
                                                : "";
                                        return (
                                            <Accordion.Item
                                                key={ws.workshopKey}
                                                eventKey={`hist-w-${pIdx}-${wIdx}`}
                                            >
                                                <Accordion.Header>
                                                    <div className="d-flex flex-column align-items-stretch w-100 gap-1 pe-2 text-start">
                                                        <div className="d-flex flex-wrap align-items-baseline justify-content-between gap-2">
                                                            <span className="fw-medium">{ws.workshopName}</span>
                                                            <span className="text-nowrap fw-semibold">
                                                                {money(ws.total)}
                                                            </span>
                                                        </div>
                                                        {roleLine ? (
                                                            <span className="small text-muted">{roleLine}</span>
                                                        ) : null}
                                                    </div>
                                                </Accordion.Header>
                                                <Accordion.Body className="px-0 pt-2 pb-1">
                                                    <div className="table-responsive">
                                                        <Table
                                                            size="sm"
                                                            bordered
                                                            className="mb-0 align-middle workshop-payroll-history-nested-table"
                                                        >
                                                            <thead className="table-light">
                                                                <tr className="small">
                                                                    <th className="text-start">Працівник</th>
                                                                    <th className="text-nowrap">Сума</th>
                                                                    <th className="text-nowrap" style={{ minWidth: 140 }}>
                                                                        ФОП (з чого)
                                                                    </th>
                                                                    <th className="text-nowrap">Оплата</th>
                                                                    <th />
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {ws.runs.map((r) => (
                                                                    <tr
                                                                        key={r._id}
                                                                        className={
                                                                            r.paid ? "table-success" : undefined
                                                                        }
                                                                    >
                                                                        <td className="text-start">
                                                                            <div className="d-flex align-items-center flex-wrap gap-1">
                                                                                <span>
                                                                                    {r.workerId?.fullName || "—"}
                                                                                    {r.workerId?.role ? (
                                                                                        <span className="text-muted small">
                                                                                            {" "}
                                                                                            (
                                                                                            {workerRoleLabelUk(
                                                                                                r.workerId.role
                                                                                            )}
                                                                                            )
                                                                                        </span>
                                                                                    ) : null}
                                                                                </span>
                                                                                {r.workerId &&
                                                                                typeof r.workerId === "object" &&
                                                                                r.workerId._id ? (
                                                                                    <WorkerRequisiteCopyButtons
                                                                                        worker={r.workerId}
                                                                                        variant="history"
                                                                                    />
                                                                                ) : null}
                                                                            </div>
                                                                        </td>
                                                                        <td className="text-nowrap">
                                                                            {money(r.totals?.grand)}
                                                                        </td>
                                                                        <td style={{ minWidth: 140 }}>
                                                                            {onPatchRunPayout ? (
                                                                                (() => {
                                                                                    const cur =
                                                                                        r.paidFromFopSlug || "";
                                                                                    const orphan =
                                                                                        cur &&
                                                                                        !(fopSlugs || []).includes(
                                                                                            cur
                                                                                        );
                                                                                    return (
                                                                                        <Form.Select
                                                                                            size="sm"
                                                                                            className="small"
                                                                                            aria-label="ФОП оплати"
                                                                                            value={cur}
                                                                                            disabled={
                                                                                                fopBusyId === r._id
                                                                                            }
                                                                                            onChange={async (e) => {
                                                                                                const v =
                                                                                                    e.target.value;
                                                                                                setFopBusyId(r._id);
                                                                                                try {
                                                                                                    await onPatchRunPayout(
                                                                                                        r._id,
                                                                                                        {
                                                                                                            paidFromFopSlug:
                                                                                                                v ===
                                                                                                                ""
                                                                                                                    ? null
                                                                                                                    : v,
                                                                                                        }
                                                                                                    );
                                                                                                } catch {
                                                                                                    /* батько */
                                                                                                } finally {
                                                                                                    setFopBusyId(
                                                                                                        null
                                                                                                    );
                                                                                                }
                                                                                            }}
                                                                                        >
                                                                                            <option value="">—</option>
                                                                                            {orphan ? (
                                                                                                <option value={cur}>
                                                                                                    {fopLabelBySlug?.[
                                                                                                        cur
                                                                                                    ] || cur}
                                                                                                </option>
                                                                                            ) : null}
                                                                                            {(fopSlugs || []).map(
                                                                                                (slug) => (
                                                                                                    <option
                                                                                                        key={slug}
                                                                                                        value={slug}
                                                                                                    >
                                                                                                        {fopLabelBySlug?.[
                                                                                                            slug
                                                                                                        ] || slug}
                                                                                                    </option>
                                                                                                )
                                                                                            )}
                                                                                        </Form.Select>
                                                                                    );
                                                                                })()
                                                                            ) : (
                                                                                <span className="small text-muted">
                                                                                    {r.paidFromFopSlug
                                                                                        ? fopLabelBySlug?.[
                                                                                              r.paidFromFopSlug
                                                                                          ] || r.paidFromFopSlug
                                                                                        : "—"}
                                                                                </span>
                                                                            )}
                                                                        </td>
                                                                        <td style={{ minWidth: 110 }}>
                                                                            <Form.Check
                                                                                type="switch"
                                                                                id={`wp-hist-paid-${r._id}`}
                                                                                className="small mb-0"
                                                                                checked={!!r.paid}
                                                                                disabled={
                                                                                    paidBusyId === r._id ||
                                                                                    !onPatchRunPaid
                                                                                }
                                                                                label={r.paid ? "Так" : "Ні"}
                                                                                onChange={async (e) => {
                                                                                    if (!onPatchRunPaid) return;
                                                                                    const v = e.target.checked;
                                                                                    setPaidBusyId(r._id);
                                                                                    try {
                                                                                        await onPatchRunPaid(
                                                                                            r._id,
                                                                                            v
                                                                                        );
                                                                                    } catch {
                                                                                        /* батько */
                                                                                    } finally {
                                                                                        setPaidBusyId(null);
                                                                                    }
                                                                                }}
                                                                            />
                                                                        </td>
                                                                        <td className="text-nowrap">
                                                                            <Button
                                                                                size="sm"
                                                                                variant="link"
                                                                                className="py-0"
                                                                                onClick={() => onOpenDetail(r)}
                                                                            >
                                                                                Деталі
                                                                            </Button>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </Table>
                                                    </div>
                                                </Accordion.Body>
                                            </Accordion.Item>
                                        );
                                    })}
                                </Accordion>
                            </Accordion.Body>
                        </Accordion.Item>
                        );
                    })}
                </Accordion>
            )}
        </div>
    );
}

function payrollLineFromSaved(L, { asDailyHardware = false } = {}) {
    const base = {
        label: L.label || "",
        productId: L.productId != null ? L.productId : null,
        qty: L.qty,
        overrideSew: L.unitPriceSew != null ? String(L.unitPriceSew) : "",
        overrideCut: L.unitPriceCut != null ? String(L.unitPriceCut) : "",
        isCustomProduct: !!L.isCustomProduct,
        note: L.note || "",
    };
    if (asDailyHardware) {
        return { ...base, payKind: "daily", isCustomProduct: true };
    }
    return base;
}

function RunDetailBody({
    run,
    isAdmin,
    onAfterMutation,
    onPatchRunPaid,
    onPatchRunPayout,
    fopSlugs,
    fopLabelBySlug,
}) {
    const [full, setFull] = useState(null);
    const [editing, setEditing] = useState(false);
    const [priceItems, setPriceItems] = useState([]);
    const [editLines, setEditLines] = useState([]);
    const [editAdj, setEditAdj] = useState([]);
    const [editNotes, setEditNotes] = useState("");
    const [editPeriodStart, setEditPeriodStart] = useState("");
    const [editPeriodEnd, setEditPeriodEnd] = useState("");
    const [editWorkerId, setEditWorkerId] = useState("");
    const [saving, setSaving] = useState(false);
    const [busy, setBusy] = useState(false);
    const [paidSaving, setPaidSaving] = useState(false);
    const [fopSaving, setFopSaving] = useState(false);

    const loadFull = useCallback(async () => {
        const r = await authFetch(`${API}/runs/${run._id}`, { credentials: "include" });
        const data = await r.json().catch(() => null);
        if (r.ok) setFull(data);
    }, [run._id, run.paid, run.paidFromFopSlug]);

    useEffect(() => {
        void loadFull();
    }, [loadFull]);

    useEffect(() => {
        if (!editing || !full?.workshopId?._id) return;
        let alive = true;
        (async () => {
            const r = await authFetch(`${API}/workshops/${full.workshopId._id}/prices`, {
                credentials: "include",
            });
            const data = await r.json().catch(() => []);
            if (alive && r.ok) setPriceItems(Array.isArray(data) ? data : []);
        })();
        return () => {
            alive = false;
        };
    }, [editing, full?.workshopId?._id]);

    const beginEdit = () => {
        if (!full) return;
        const w0 = full.workerId;
        const asHw = typeof w0 === "object" && w0?.role === "hardware";
        setEditLines((full.lines || []).map((L) => payrollLineFromSaved(L, { asDailyHardware: asHw })));
        setEditAdj(
            (full.adjustments || []).map((a) => ({
                kind: a.kind,
                amount: a.amount,
                note: a.note || "",
                lineIndex: a.lineIndex ?? null,
            }))
        );
        setEditNotes(full.notes || "");
        setEditPeriodStart(new Date(full.periodStart).toISOString().slice(0, 10));
        setEditPeriodEnd(new Date(full.periodEnd).toISOString().slice(0, 10));
        const w = full.workerId;
        setEditWorkerId(w && typeof w === "object" && w._id ? String(w._id) : w ? String(w) : "");
        setEditing(true);
    };

    const cancelEdit = () => {
        setEditing(false);
        setPriceItems([]);
    };

    const saveEdit = async () => {
        if (!full) return;
        const ws = full.workshopId;
        if (ws?.trackWorkers && !editWorkerId) {
            alert("Оберіть працівника");
            return;
        }
        setSaving(true);
        try {
            const r = await authFetch(`${API}/runs/${full._id}`, {
                method: "PATCH",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    periodStart: new Date(editPeriodStart).toISOString(),
                    periodEnd: new Date(editPeriodEnd + "T23:59:59").toISOString(),
                    workerId: ws?.trackWorkers ? editWorkerId : null,
                    lines: editLines.map((L) => ({
                        label: L.label,
                        productId: L.productId,
                        qty: L.qty,
                        overrideSew: L.overrideSew === "" ? undefined : L.overrideSew,
                        overrideCut: L.overrideCut === "" ? undefined : L.overrideCut,
                        isCustomProduct: L.isCustomProduct,
                        note: L.note,
                        ...(L.payKind === "daily" ? { payKind: "daily" } : {}),
                    })),
                    adjustments: editAdj.map((a) => ({
                        kind: a.kind,
                        amount: Number(a.amount) || 0,
                        note: a.note || "",
                        lineIndex: a.lineIndex,
                    })),
                    notes: editNotes,
                }),
            });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) {
                alert(data.error || "Помилка збереження");
                return;
            }
            onAfterMutation?.();
        } finally {
            setSaving(false);
        }
    };

    const deleteRun = async () => {
        if (!full || !window.confirm("Видалити цю виплату з історії?")) return;
        setBusy(true);
        try {
            const r = await authFetch(`${API}/runs/${full._id}`, { method: "DELETE", credentials: "include" });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) {
                alert(data.error || "Помилка");
                return;
            }
            onAfterMutation?.();
        } finally {
            setBusy(false);
        }
    };

    const savePaid = async (paid) => {
        if (!full || !onPatchRunPaid) return;
        setPaidSaving(true);
        try {
            await onPatchRunPaid(full._id, paid);
            /* після merge у батька змінюється run.paid → loadFull підтягне актуальний документ */
        } catch {
            /* помилка в батьківському стані */
        } finally {
            setPaidSaving(false);
        }
    };

    const savePaidFromFop = async (slugOrEmpty) => {
        if (!full || !onPatchRunPayout) return;
        setFopSaving(true);
        try {
            await onPatchRunPayout(full._id, {
                paidFromFopSlug: slugOrEmpty === "" ? null : slugOrEmpty,
            });
        } catch {
            /* батько */
        } finally {
            setFopSaving(false);
        }
    };

    if (!full) return <Spinner size="sm" animation="border" />;

    const separate = full.workshopId?.separateCutPricing;
    const trackWorkers = full.workshopId?.trackWorkers;
    const workshopWorkers = (full.workshopId?.workers || []).filter((w) => w.active !== false);
    const readWid = full.workerId?._id || full.workerId;
    const readFlags = payrollUiFlags(!!trackWorkers, readWid, workshopWorkers, !!separate);
    const wsId = full.workshopId?._id;

    const updateLine = (i, patch) => {
        setEditLines((prev) => prev.map((L, j) => (j === i ? { ...L, ...patch } : L)));
    };

    if (editing) {
        const editWorkerObj = workshopWorkers.find((w) => String(w._id) === String(editWorkerId));
        const editHardwarePayroll = editWorkerObj?.role === "hardware";
        const { showRows: editShowRows, showSewCol: eSew, showCutCol: eCut } = payrollUiFlags(
            !!trackWorkers,
            editWorkerId,
            workshopWorkers,
            !!separate
        );
        const addRowDisabled =
            !wsId ||
            (!editHardwarePayroll && priceItems.length === 0) ||
            (!!trackWorkers && !editWorkerId);

        return (
            <div>
                <p className="small text-muted mb-2">{full.workshopId?.name}</p>
                <div className="row g-2 mb-2">
                    <div className="col-md-4">
                        <Form.Label className="small text-muted mb-0">Період з (пн)</Form.Label>
                        <AppDateField
                            size="sm"
                            value={editPeriodStart}
                            onChange={(ymd) => {
                                if (!ymd) return;
                                const { start, end } = snapToPayrollWeek(ymd);
                                setEditPeriodStart(start);
                                setEditPeriodEnd(end);
                            }}
                        />
                    </div>
                    <div className="col-md-4">
                        <Form.Label className="small text-muted mb-0">Період по (нд)</Form.Label>
                        <AppDateField
                            size="sm"
                            value={editPeriodEnd}
                            onChange={(ymd) => {
                                if (!ymd) return;
                                const { start, end } = snapToPayrollWeek(ymd);
                                setEditPeriodStart(start);
                                setEditPeriodEnd(end);
                            }}
                        />
                    </div>
                    {trackWorkers && (
                        <div className="col-md-4">
                            <Form.Label className="small text-muted mb-0">Працівник</Form.Label>
                            <Form.Select
                                size="sm"
                                value={editWorkerId}
                                onChange={(e) => setEditWorkerId(e.target.value)}
                            >
                                <option value="">—</option>
                                {workshopWorkers.map((w) => (
                                    <option key={w._id} value={w._id}>
                                        {w.fullName} ({workerRoleLabelUk(w.role)})
                                    </option>
                                ))}
                            </Form.Select>
                        </div>
                    )}
                </div>

                {trackWorkers && !editWorkerId && (
                    <p className="small text-muted mb-2">Оберіть працівника, щоб редагувати рядки виплати.</p>
                )}

                {editShowRows && (
                    <>
                        {editHardwarePayroll ? (
                            <>
                                <div className="mb-2">
                                    <strong className="small">Оплата за робочі дні</strong>
                                    <p className="small text-muted mb-0">
                                        Дні та ставка (грн/день); збереження — як у новому розрахунку.
                                    </p>
                                </div>
                                <Table responsive bordered size="sm" className="align-middle mb-3">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Опис</th>
                                            <th style={{ width: 100 }}>Днів</th>
                                            <th style={{ width: 120 }}>Грн за день</th>
                                            <th style={{ width: 110 }} className="text-end">
                                                Сума
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {editLines.map((L, i) => {
                                            const lm = lineEffectiveMoney(
                                                L,
                                                priceItems,
                                                full.workshopId,
                                                "hardware"
                                            );
                                            return (
                                                <tr key={i}>
                                                    <td className="small">{L.label}</td>
                                                    <td>
                                                        <Form.Control
                                                            type="number"
                                                            min={0}
                                                            step={0.5}
                                                            size="sm"
                                                            value={L.qty}
                                                            onChange={(e) => updateLine(i, { qty: e.target.value })}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            type="number"
                                                            min={0}
                                                            step={1}
                                                            size="sm"
                                                            value={L.overrideSew}
                                                            onChange={(e) =>
                                                                updateLine(i, { overrideSew: e.target.value })
                                                            }
                                                        />
                                                    </td>
                                                    <td className="text-end small fw-medium">{money(lm.lineTotal)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </>
                        ) : (
                            <>
                                <div className="d-flex align-items-center gap-2 mb-2">
                                    <strong className="small">Товари</strong>
                                    <Button
                                        size="sm"
                                        variant="outline-secondary"
                                        disabled={addRowDisabled}
                                        title={
                                            addRowDisabled
                                                ? "Потрібні цех, прайс, за наявності обліку — обраний працівник"
                                                : "Додати товар"
                                        }
                                        onClick={() => setEditLines((p) => [...p, emptyLine()])}
                                    >
                                        +
                                    </Button>
                                </div>
                                <Table responsive bordered size="sm" className="align-middle mb-3">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Товар</th>
                                            <th style={{ width: 70 }}>К-сть</th>
                                            {eSew && <th style={{ width: 100 }}>Ціна пошив<br /><span className="text-muted fw-normal" style={{ fontSize: "0.75em" }}>з прайсу</span></th>}
                                            {eCut && <th style={{ width: 100 }}>Ціна крій<br /><span className="text-muted fw-normal" style={{ fontSize: "0.75em" }}>з прайсу</span></th>}
                                            <th style={{ width: 40 }} />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {editLines.map((L, i) => {
                                        const resolved = resolvePriceItemForLine(priceItems, L.productId, L.label);
                                        return (
                                            <tr key={i}>
                                                <td>
                                                    <PriceListSuggestInput
                                                        workshopId={wsId}
                                                        priceItems={priceItems}
                                                        disabled={!wsId || priceItems.length === 0}
                                                        value={L.label}
                                                        onChange={(v) => updateLine(i, { label: v })}
                                                        onPickProduct={(pid) => updateLine(i, { productId: pid })}
                                                    />
                                                </td>
                                                <td>
                                                    <Form.Control
                                                        type="number"
                                                        min={0}
                                                        size="sm"
                                                        value={L.qty}
                                                        onChange={(e) => updateLine(i, { qty: e.target.value })}
                                                    />
                                                </td>
                                                {eSew && (
                                                    <td>
                                                        <Form.Control
                                                            type="number"
                                                            min={0}
                                                            step="0.01"
                                                            size="sm"
                                                            value={
                                                                L.overrideSew !== ""
                                                                    ? L.overrideSew
                                                                    : resolved
                                                                      ? String(resolved.priceSew ?? "")
                                                                      : ""
                                                            }
                                                            onChange={(e) =>
                                                                updateLine(i, { overrideSew: e.target.value })
                                                            }
                                                        />
                                                    </td>
                                                )}
                                                {eCut && (
                                                    <td>
                                                        <Form.Control
                                                            type="number"
                                                            min={0}
                                                            step="0.01"
                                                            size="sm"
                                                            value={
                                                                L.overrideCut !== ""
                                                                    ? L.overrideCut
                                                                    : resolved
                                                                      ? String(resolved.priceCut ?? "")
                                                                      : ""
                                                            }
                                                            onChange={(e) =>
                                                                updateLine(i, { overrideCut: e.target.value })
                                                            }
                                                        />
                                                    </td>
                                                )}
                                                <td>
                                                    <Button
                                                        variant="link"
                                                        className="text-danger p-0"
                                                        onClick={() =>
                                                            setEditLines((p) => p.filter((_, j) => j !== i))
                                                        }
                                                    >
                                                        <Trash2 size={15} aria-hidden />
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                        })}
                                    </tbody>
                                </Table>
                            </>
                        )}

                        <div className="d-flex align-items-center gap-2 mb-2">
                            <strong className="small">Штрафи / доплати</strong>
                            <Button
                                size="sm"
                                variant="outline-secondary"
                                title="Додати штраф або доплату"
                                onClick={() =>
                                    setEditAdj((p) => [...p, { kind: "bonus", amount: 0, note: "", lineIndex: null }])
                                }
                            >
                                +
                            </Button>
                        </div>
                        {editAdj.map((a, i) => (
                            <div key={i} className="d-flex flex-wrap gap-2 align-items-center mb-1">
                                <Form.Select
                                    size="sm"
                                    style={{ width: 120 }}
                                    value={a.kind}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setEditAdj((p) => p.map((x, j) => (j === i ? { ...x, kind: v } : x)));
                                    }}
                                >
                                    <option value="bonus">Доплата</option>
                                    <option value="penalty">Штраф</option>
                                </Form.Select>
                                <Form.Control
                                    type="number"
                                    size="sm"
                                    style={{ width: 100 }}
                                    value={a.amount}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setEditAdj((p) => p.map((x, j) => (j === i ? { ...x, amount: v } : x)));
                                    }}
                                />
                                <Form.Control
                                    size="sm"
                                    placeholder="Коментар"
                                    value={a.note}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setEditAdj((p) => p.map((x, j) => (j === i ? { ...x, note: v } : x)));
                                    }}
                                    style={{ minWidth: 160 }}
                                />
                                <Button
                                    variant="link"
                                    className="text-danger p-0"
                                    onClick={() => setEditAdj((p) => p.filter((_, j) => j !== i))}
                                >
                                    видалити
                                </Button>
                            </div>
                        ))}

                        <Form.Group className="mb-3">
                            <Form.Label className="small text-muted">Нотатка</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={editNotes}
                                onChange={(e) => setEditNotes(e.target.value)}
                                size="sm"
                            />
                        </Form.Group>
                    </>
                )}

                <div className="d-flex flex-wrap gap-2">
                    {editShowRows && (
                        <Button size="sm" variant="success" disabled={saving} onClick={() => void saveEdit()}>
                            {saving ? <Spinner size="sm" animation="border" /> : "Зберегти зміни"}
                        </Button>
                    )}
                    <Button size="sm" variant="outline-secondary" disabled={saving} onClick={cancelEdit}>
                        Скасувати
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div>
            <p className="small text-muted mb-2">
                {full.workshopId?.name} ·{" "}
                {full.workerId?.fullName || "цех без імені працівника"}
                {full.workerId?.role ? ` (${workerRoleLabelUk(full.workerId.role)})` : ""}
            </p>
            {onPatchRunPaid && (
                <Form.Check
                    type="switch"
                    id="wp-detail-paid"
                    className="small mb-2"
                    checked={!!full.paid}
                    disabled={paidSaving}
                    label={full.paid ? "Оплачено" : "Не оплачено"}
                    onChange={(e) => void savePaid(e.target.checked)}
                />
            )}
            {onPatchRunPayout && (
                <Form.Group className="small mb-3" style={{ maxWidth: 320 }}>
                    <Form.Label className="small text-muted mb-1">ФОП (з чого оплачувати / оплачено)</Form.Label>
                    {(() => {
                        const cur = full.paidFromFopSlug || "";
                        const orphan = cur && !(fopSlugs || []).includes(cur);
                        return (
                            <Form.Select
                                size="sm"
                                value={cur}
                                disabled={fopSaving}
                                onChange={(e) => void savePaidFromFop(e.target.value)}
                            >
                                <option value="">—</option>
                                {orphan ? (
                                    <option value={cur}>{fopLabelBySlug?.[cur] || cur}</option>
                                ) : null}
                                {(fopSlugs || []).map((slug) => (
                                    <option key={slug} value={slug}>
                                        {fopLabelBySlug?.[slug] || slug}
                                    </option>
                                ))}
                            </Form.Select>
                        );
                    })()}
                </Form.Group>
            )}
            <Table size="sm" bordered>
                <thead>
                    <tr>
                        <th>Товар</th>
                        <th>К-сть</th>
                        {full.workerId?.role === "hardware" ? (
                            <th className="text-end">Грн за день</th>
                        ) : (
                            (readFlags.showSewCol || readFlags.showCutCol) && (
                                <th className="text-end">За 1 шт</th>
                            )
                        )}
                        <th className="text-end">Усього</th>
                    </tr>
                </thead>
                <tbody>
                    {(full.computed || []).map((L, i) => {
                        const unitSew = Number(L.unitPriceSew) || 0;
                        const unitCut = Number(L.unitPriceCut) || 0;
                        const unitPiece = Math.round((unitSew + unitCut) * 100) / 100;
                        const isHw = full.workerId?.role === "hardware";
                        return (
                            <tr key={i}>
                                <td>{L.label}</td>
                                <td>{L.qty}</td>
                                {isHw ? (
                                    <td className="text-end text-nowrap small">{money(unitSew)}</td>
                                ) : (
                                    (readFlags.showSewCol || readFlags.showCutCol) && (
                                        <td className="text-end text-nowrap small text-muted">
                                            {unitPiece > 0 ? money(unitPiece) : "—"}
                                        </td>
                                    )
                                )}
                                <td className="text-end text-nowrap small fw-medium">{money(L.lineTotal)}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
            <div className="small">
                <div>
                    Разом по рядках:{" "}
                    {money(
                        (Number(full.totalsCheck?.linesSew) || 0) +
                            (Number(full.totalsCheck?.linesCut) || 0)
                    )}
                </div>
                <div>Коригування: {money(full.totalsCheck?.adjustments)}</div>
                <strong>Всього: {money(full.totalsCheck?.grand)}</strong>
            </div>
            {full.notes && <p className="mt-2 small">{full.notes}</p>}
            {isAdmin && (
                <div className="mt-3 d-flex flex-wrap gap-2">
                    <Button size="sm" variant="primary" onClick={beginEdit}>
                        Редагувати виплату
                    </Button>
                    <Button size="sm" variant="outline-danger" disabled={busy} onClick={() => void deleteRun()}>
                        {busy ? <Spinner size="sm" animation="border" /> : "Видалити запис"}
                    </Button>
                </div>
            )}
        </div>
    );
}
